import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MapPin, FileSearch, Shield, Compass, ArrowRight, Check, ChevronLeft, ChevronRight, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Reveal, RevealGroup, RevealItem } from '../components/motion/Reveal';
import Seo from '../components/Seo';
import Testimonials from '../components/Testimonials';
import NewsTicker from '../components/NewsTicker';
import PropertyHubSection from '../components/PropertyHubSection';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroImageY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const heroContentY = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const educationSlides = [
    {
      title: "Why Survey Search Matters",
      content: "Before purchasing any property, a comprehensive survey search verifies land authenticity, checks government acquisition status, and confirms legitimate ownership. This critical step protects your investment from fraud and legal disputes.",
      highlight: "Survey search is your shield against land fraud"
    },
    {
      title: "The Surveyor's Role in Every Step",
      content: "From initial land verification to final documentation, surveyors ensure accuracy at every stage. They verify boundaries, authenticate documents, check government records, and provide legally compliant documentation for smooth property transactions.",
      highlight: "Professional guidance through every transaction phase"
    },
    {
      title: "Consult Your Surveyor First",
      content: "Before signing any land agreement or making payments, consult with a licensed surveyor. They identify red flags, verify seller claims, check for encumbrances, and ensure the property matches legal descriptions—potentially saving you millions.",
      highlight: "Prevention is better than cure in real estate"
    },
    {
      title: "Your Surveyor: Your Best Friend",
      content: "In Nigerian real estate, your surveyor is your most valuable ally. They protect you from fraudulent sales, ensure legal compliance, provide expert guidance, and offer peace of mind throughout your property journey.",
      highlight: "Trust, expertise, and protection in every transaction"
    }
  ];

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % educationSlides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [educationSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % educationSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + educationSlides.length) % educationSlides.length);
  };

  const services = [
    {
      icon: MapPin,
      title: "Land Surveying",
      description: "Precise cadastral & topographic surveys"
    },
    {
      icon: FileSearch,
      title: "Survey Search",
      description: "Complete verification & authentication"
    },
    {
      icon: Shield,
      title: "Boundary Identification",
      description: "Expert boundary re-establishment"
    },
    {
      icon: Compass,
      title: "Documentation",
      description: "Property title & compliance support"
    }
  ];

  const trustPoints = [
    "Licensed & Experienced Surveyors",
    "Lagos & Nigeria-wide Expertise",
    "Government Acquisition Checks",
    "Accurate & Legally Compliant"
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title="Land Surveyors in Lagos | Versatile Geopick NIG Ltd"
        description="Licensed land surveyors in Lagos, Nigeria. Survey search, property verification, boundary identification, C of O documentation and land status checks."
      />
      {/* Hero Section - Minimal & Elegant with Parallax */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 pt-20">
        <motion.div className="absolute inset-0 z-0 opacity-40" style={{ y: heroImageY }}>
          <img 
            src="https://images.unsplash.com/photo-1628158088803-4200c2be52f1"
            alt="Professional Surveying"
            className="w-full h-[115%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-50/80 via-stone-50/60 to-stone-100/90"></div>
        </motion.div>
        
        <motion.div 
          className="relative z-10 max-w-5xl mx-auto px-6 text-center"
          style={{ y: heroContentY, opacity: heroOpacity }}
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.18, delayChildren: 0.2 } } }}
        >
          <motion.h1 
            variants={{ hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } } }}
            className="text-5xl md:text-7xl lg:text-8xl font-light text-stone-800 mb-8 tracking-tight leading-tight"
          >
            Trusted Land Survey &<br />
            Property <span className="font-semibold text-emerald-700">Verification<br />in Nigeria</span>
          </motion.h1>
          
          <motion.p 
            variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
            className="text-xl md:text-2xl text-stone-600 mb-16 font-light max-w-3xl mx-auto"
          >
            Precision. Compliance. Confidence in every survey.
          </motion.p>

          <motion.div 
            variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } } }}
            className="flex flex-wrap justify-center gap-6 mb-8"
          >
            <Button 
              size="lg" 
              className="bg-emerald-700 hover:bg-emerald-800 text-white px-12 py-8 text-lg font-normal shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl"
              asChild
            >
              <Link to="/survey-search">
                Verify Land <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-stone-300 text-stone-700 hover:bg-stone-100 hover:border-stone-400 px-12 py-8 text-lg font-normal transition-all duration-300 rounded-2xl"
              asChild
            >
              <Link to="/services">
                Request Survey
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* Services Preview - Clean Cards */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-20">
            <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3">What We Do</p>
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 tracking-tight">
              Our Services
            </h2>
          </Reveal>

          <RevealGroup className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <RevealItem key={index}>
                <Card 
                  className="p-8 h-full border border-stone-200 hover:border-emerald-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-2 bg-white group"
                >
                  <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-colors duration-300">
                    <Icon className="h-7 w-7 text-emerald-700" />
                  </div>
                  <h3 className="text-xl font-medium text-stone-800 mb-3">{service.title}</h3>
                  <p className="text-stone-600 leading-relaxed">{service.description}</p>
                </Card>
                </RevealItem>
              );
            })}
          </RevealGroup>

          <Reveal className="text-center mt-16" delay={0.2}>
            <Button 
              variant="ghost"
              className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 text-lg font-normal px-8 py-6"
              asChild
            >
              <Link to="/services">
                View Full Services <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* Educational Slider - Why Surveyors Matter */}
      <section className="py-32 bg-gradient-to-br from-emerald-700 to-emerald-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <Reveal className="text-center mb-12">
            <p className="text-emerald-200 font-medium tracking-wider uppercase text-sm mb-3">Education</p>
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-4">
              Why Your Surveyor Matters
            </h2>
            <p className="text-emerald-100 text-lg">
              Understanding the critical role of professional surveyors in your property journey
            </p>
          </Reveal>

          <Reveal direction="scale" delay={0.15} className="relative">
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20 p-12 min-h-[400px] flex flex-col justify-center">
              <div className="transition-all duration-500 ease-in-out">
                <h3 className="text-3xl font-medium text-white mb-6">
                  {educationSlides[currentSlide].title}
                </h3>
                <p className="text-xl text-emerald-50 leading-relaxed mb-8">
                  {educationSlides[currentSlide].content}
                </p>
                <div className="inline-block px-6 py-3 bg-emerald-600 rounded-2xl border border-emerald-400">
                  <p className="text-white font-medium">
                    {educationSlides[currentSlide].highlight}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 border border-white/30"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 border border-white/30"
              aria-label="Next slide"
            >
              <ChevronRight className="h-6 w-6 text-white" />
            </button>

            {/* Slide Indicators */}
            <div className="flex justify-center gap-3 mt-8">
              {educationSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentSlide
                      ? 'w-12 h-3 bg-white'
                      : 'w-3 h-3 bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </Reveal>

          <Reveal className="text-center mt-12" delay={0.25}>
            <Button 
              size="lg"
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-12 py-6 text-lg font-normal shadow-xl hover:shadow-2xl transition-all duration-300"
              asChild
            >
              <Link to="/survey-search">
                Start Your Survey Search <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* Why Clients Trust Us - Visual, Not Text Heavy */}
      <section className="py-32 bg-gradient-to-br from-stone-50 to-amber-50/20">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center mb-20">
            <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3">Why Choose Us</p>
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 tracking-tight">
              Built on Trust
            </h2>
          </Reveal>

          <RevealGroup className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto" stagger={0.1}>
            {trustPoints.map((point, index) => (
              <RevealItem key={index} direction="scale">
              <div 
                className="flex items-center gap-4 bg-white p-6 rounded-2xl border border-stone-200 hover:border-emerald-200 transition-all duration-300 h-full"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="h-6 w-6 text-emerald-700" />
                </div>
                <p className="text-stone-700 font-medium">{point}</p>
              </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Client Testimonials */}
      <Testimonials />

      {/* Property Hub - Live News + Latest Articles */}
      <NewsTicker />
      <PropertyHubSection />

      {/* CTA Section - Soft & Centered */}
      <section className="py-32 bg-emerald-700">        <Reveal className="max-w-4xl mx-auto px-6 text-center" direction="scale">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-8 leading-tight">
            Verify your land before you<br />buy or build.
          </h2>
          <Button 
            size="lg"
            className="bg-white text-emerald-700 hover:bg-stone-50 px-12 py-7 text-lg font-medium shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            asChild
          >
            <Link to="/survey-search">
              Start Survey Search
            </Link>
          </Button>
        </Reveal>
      </section>

      {/* Quick Access Links */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <RevealGroup className="grid md:grid-cols-2 gap-8" stagger={0.15}>
            <RevealItem direction="left">
            <Card className="border-2 h-full border-stone-200 hover:border-emerald-300 transition-all duration-300 hover:-translate-y-2 group cursor-pointer">
              <Link to="/consultation">
                <CardContent className="p-10">
                  <Calendar className="h-12 w-12 text-emerald-700 mb-6 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-3xl font-medium text-stone-800 mb-4">Book a Consultation</h3>
                  <p className="text-lg text-stone-600 mb-6">
                    Get expert advice from licensed surveyors. Free phone/video consultations available.
                  </p>
                  <span className="text-emerald-700 font-medium inline-flex items-center gap-2">
                    Schedule Now <ArrowRight className="h-5 w-5" />
                  </span>
                </CardContent>
              </Link>
            </Card>
            </RevealItem>

            <RevealItem direction="right">
            <Card className="border-2 h-full border-stone-200 hover:border-emerald-300 transition-all duration-300 hover:-translate-y-2 group cursor-pointer">
              <Link to="/fee-scale">
                <CardContent className="p-10">
                  <DollarSign className="h-12 w-12 text-emerald-700 mb-6 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-3xl font-medium text-stone-800 mb-4">View Fee Scale</h3>
                  <p className="text-lg text-stone-600 mb-6">
                    Transparent pricing for survey works across Lagos State Local Government Areas.
                  </p>
                  <span className="text-emerald-700 font-medium inline-flex items-center gap-2">
                    Check Fees <ArrowRight className="h-5 w-5" />
                  </span>
                </CardContent>
              </Link>
            </Card>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Contact Preview */}
      <section className="py-24 bg-white">
        <Reveal className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-stone-600 text-lg mb-6">
            Ready to discuss your project?
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="tel:+2348034494507"
              className="text-emerald-700 hover:text-emerald-800 font-medium text-lg transition-colors duration-300"
            >
              +234 803 449 4507
            </a>
            <span className="text-stone-300">|</span>
            <a 
              href="mailto:Versatilegeopick@gmail.com"
              className="text-emerald-700 hover:text-emerald-800 font-medium text-lg transition-colors duration-300"
            >
              Versatilegeopick@gmail.com
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
